import type { ReactNode } from "react";
import { useMemo } from "react";
import { createFilterOptions } from "@mui/material";
import type { Metier } from "models";
import { MetierBox } from "components/Business";
import { stringUtil } from "@sdeapps/react-core";
import { useMetiers } from "hooks";
import { withComponentErrorBoundary } from "utils/errorHandling";
import { MultiAutoComplete } from "./MultiAutoComplete";
import { multiAutoClassNames } from "utils";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: ({ competence, libelle }: Metier) => {
    const normalizedString = stringUtil.normalize(`${competence} ${libelle}`);

    // On a besoin des deux versions du libellé dans le cas où:
    // l'utilisateur écrit le libellé avec des caractères spéciaux ou alors des apostrophes
    // Exemple: Chargé d'autosurveillance DO et diag permanent
    return `${normalizedString} ${libelle}`;
  },
});

interface MetierMultiAutoProps {
  value: Array<Metier>;
  onChange?: (metier: Array<Metier>) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  groupesParNiveau?: boolean;
}

function MetierMultiAuto({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  groupesParNiveau = false,
}: Readonly<MetierMultiAutoProps>): ReactNode {
  const { metiers, isMetiersLoading } = useMetiers();

  const sortedMetiers = useMemo(
    () =>
      groupesParNiveau ? metiers.toSorted((a, b) => a.niveau.localeCompare(b.niveau)) : metiers,
    [groupesParNiveau, metiers]
  );

  return (
    <MultiAutoComplete
      value={value}
      onChange={onChange}
      options={sortedMetiers}
      filterOptions={filterOptions}
      groupBy={groupesParNiveau ? (option) => `Métiers niveau ${option.niveau}` : undefined}
      size={size}
      singleItemLabel="Métier"
      isLoading={isMetiersLoading}
      label={label}
      placeholder={placeholder}
      renderOption={(props, metier, { selected }) => (
        <li {...props} key={metier.code}>
          <div
            className={`${multiAutoClassNames.AUTOCOMPLETE_CSS_CHECKBOX} ${selected ? multiAutoClassNames.CHECKED : multiAutoClassNames.UNCHECKED}`}
          />
          <MetierBox metier={metier} noWrap />
        </li>
      )}
    />
  );
}

export const MetierMultiAutoWithErrorBoundary = withComponentErrorBoundary(MetierMultiAuto);
